import type { Category } from '@/types/market'

// Sort options supported by both platforms
export type SortKey = 'volume' | 'volume24h' | 'liquidity' | 'openInterest' | 'startDate'

// Kalshi-specific status values
export type KalshiStatus = 'initialized' | 'active' | 'inactive' | 'closed' | 'determined'

export type ListParams = {
	limit: number
	offset: number
	sort: SortKey
	status: 'active' | 'closed' | 'all'
	search?: string
	categories?: Category[]
	minVolume?: number
	maxVolume?: number
	minLiquidity?: number
	maxLiquidity?: number
	// Kalshi-specific params
	kalshiStatus?: KalshiStatus[]
	isInitialized?: boolean
	// Polymarket-specific params
	polymarketActive?: boolean
	polymarketClosed?: boolean
	polymarketArchived?: boolean
}

export type PaginatedResponse<T> = {
	data: T[]
	pagination: {
		offset: number
		limit: number
		nextOffset: number | null
		hasMore: boolean
	}
}

export const SORT_KEYS = [
	'volume',
	'volume24h',
	'liquidity',
	'openInterest',
	'startDate',
] as const satisfies readonly SortKey[]

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value))
}

function parseNumber(value: string | null, fallback: number): number {
	if (!value) {
		return fallback
	}
	const n = Number(value)
	return Number.isFinite(n) ? n : fallback
}

function parseOptionalNumber(value: string | null): number | undefined {
	if (!value) {
		return undefined
	}
	const n = Number(value)
	return Number.isFinite(n) ? n : undefined
}

function parseCategories(value: string | null): Category[] | undefined {
	if (!value) {
		return undefined
	}
	const parts = value
		.split(',')
		.map(s => s.trim())
		.filter(Boolean)
	return parts.length > 0 ? (parts as Category[]) : undefined
}

const KALSHI_STATUS_VALUES: KalshiStatus[] = [
	'initialized',
	'active',
	'inactive',
	'closed',
	'determined',
]

function parseKalshiStatus(value: string | null): KalshiStatus[] | undefined {
	if (!value) {
		return undefined
	}
	const parts = value
		.split(',')
		.map(s => s.trim().toLowerCase())
		.filter((s): s is KalshiStatus => KALSHI_STATUS_VALUES.includes(s as KalshiStatus))
	return parts.length > 0 ? parts : undefined
}

function parseOptionalBoolean(value: string | null): boolean | undefined {
	if (value === null) {
		return undefined
	}
	return value === 'true' || value === '1'
}

export function parseListParams(searchParams: URLSearchParams): ListParams {
	const limit = clamp(parseNumber(searchParams.get('limit'), 50), 1, 100)
	const offset = Math.max(0, parseNumber(searchParams.get('offset'), 0))

	const sortRaw = searchParams.get('sort')
	const sort: SortKey =
		sortRaw && SORT_KEYS.includes(sortRaw as SortKey) ? (sortRaw as SortKey) : 'volume'

	const statusRaw = searchParams.get('status')
	const status = statusRaw === 'active' ? 'active' : 'all'

	return {
		limit,
		offset,
		sort,
		status,
		search: searchParams.get('search') || undefined,
		categories: parseCategories(searchParams.get('categories')),
		minVolume: parseOptionalNumber(searchParams.get('minVolume')),
		maxVolume: parseOptionalNumber(searchParams.get('maxVolume')),
		minLiquidity: parseOptionalNumber(searchParams.get('minLiquidity')),
		maxLiquidity: parseOptionalNumber(searchParams.get('maxLiquidity')),
		// Kalshi-specific
		kalshiStatus: parseKalshiStatus(searchParams.get('kalshiStatus')),
		isInitialized: parseOptionalBoolean(searchParams.get('isInitialized')),
		// Polymarket-specific
		polymarketActive: parseOptionalBoolean(searchParams.get('active')),
		polymarketClosed: parseOptionalBoolean(searchParams.get('closed')),
		polymarketArchived: parseOptionalBoolean(searchParams.get('archived')),
	}
}

// Map our sort keys to platform-specific API params
export const KALSHI_SORT_MAP: Record<SortKey, string> = {
	volume: 'volume',
	volume24h: 'volume24h',
	liquidity: 'liquidity',
	openInterest: 'openInterest',
	startDate: 'startDate',
}

export const POLYMARKET_SORT_MAP: Record<SortKey, string> = {
	volume: 'volume',
	volume24h: 'volume24hr',
	liquidity: 'liquidity',
	openInterest: 'liquidity', // Polymarket doesn't have openInterest, fallback
	startDate: 'startDate',
}

// For markets endpoint, Polymarket needs numeric field names for proper sorting
export const POLYMARKET_MARKET_SORT_MAP: Record<SortKey, string> = {
	volume: 'volumeNum',
	volume24h: 'volume24hr',
	liquidity: 'liquidityNum',
	openInterest: 'liquidityNum',
	startDate: 'startDate',
}

export function buildPagination<T>(
	data: T[],
	offset: number,
	limit: number
): PaginatedResponse<T>['pagination'] {
	const hasMore = data.length === limit
	return {
		offset,
		limit,
		nextOffset: hasMore ? offset + data.length : null,
		hasMore,
	}
}
